import { useMemo } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useMutation } from "convex/react";
import type { FunctionReturnType } from "convex/server";
import { createColumnHelper } from "@tanstack/react-table";
import { Badge, Box, Button, Group, HStack, Stack, Text } from "@chakra-ui/react";
import { LuCheck, LuUndo2 } from "react-icons/lu";
import { api } from "@convex/_generated/api";
import { useQuery } from "@/lib/convex";
import { useViewerAccess } from "@/lib/access";
import { flag, searchUpdate } from "@/lib/search";
import { DataTable } from "../components/DataTable";
import { FilterCount } from "../components/FilterCount";
import { PageHeader } from "../components/PageHeader";
import { TableSkeleton } from "../components/TableSkeleton";
import { TextLink } from "../components/TextLink";
import { formatAuditTime } from "../components/auditDisplay";

type FeedbackRow = FunctionReturnType<typeof api.appFeedback.list>[number];

const breadcrumbs = [
  { label: "CeMMbase", to: "/" },
  { label: "Administration", to: "/admin" },
  { label: "Feedback" },
];

export function validateAppFeedbackSearch(search: Record<string, unknown>): { all?: true } {
  return { all: flag(search.all) };
}

const column = createColumnHelper<FeedbackRow>();

/**
 * Administration > Feedback: what people sent with "Send feedback".
 *
 * Open ones by default, newest first; marking one dealt with takes it off
 * the list without deleting what was said.
 */
export function AppFeedbackPage() {
  const access = useViewerAccess();
  const rows = useQuery(api.appFeedback.list, access?.isSysAdmin ? {} : "skip");
  const setHandled = useMutation(api.appFeedback.setHandled);
  const showAll = useSearch({ from: "/_authed/admin/feedback" }).all === true;
  const navigate = useNavigate({ from: "/admin/feedback" });
  const setShowAll = (next: boolean) =>
    void navigate({ search: { all: next ? true : undefined }, ...searchUpdate });

  const columns = useMemo(() => [
    column.accessor("createdAt", {
      header: "When",
      cell: ({ getValue }) => <Text whiteSpace="nowrap">{formatAuditTime(getValue())}</Text>,
    }),
    column.accessor("senderName", {
      header: "From",
      cell: ({ row }) => (
        <Stack gap={1} align="start">
          <Text whiteSpace="nowrap">{row.original.senderName}</Text>
          {row.original.lookingAsName === null ? null : (
            <Badge colorPalette="orange" variant="subtle" size="sm">looking as {row.original.lookingAsName}</Badge>
          )}
        </Stack>
      ),
    }),
    column.accessor("message", {
      header: "Feedback",
      // In full: the words are the point of the page.
      cell: ({ row }) => (
        <Stack gap={1}>
          <Text whiteSpace="pre-wrap">{row.original.message}</Text>
          <Text fontSize="xs" color="fg.muted">
            On{" "}
            <TextLink>
              {/* A plain link: the path carries its query string, which the
                  router's Link would not parse out of `to`. */}
              <a href={row.original.pagePath}>{row.original.pagePath}</a>
            </TextLink>
          </Text>
        </Stack>
      ),
    }),
    column.display({
      id: "handled",
      header: "",
      cell: ({ row }) => row.original.handledAt === null ? (
        <Button size="xs" variant="outline" onClick={() => void setHandled({ feedbackId: row.original._id, handled: true })}>
          <LuCheck />
          Done
        </Button>
      ) : (
        <Button size="xs" variant="ghost" onClick={() => void setHandled({ feedbackId: row.original._id, handled: false })}>
          <LuUndo2 />
          Reopen
        </Button>
      ),
    }),
  ], [setHandled]);

  const open = (rows ?? []).filter((row) => row.handledAt === null);
  const shown = showAll ? (rows ?? []) : open;

  return (
    <Box minH="full" bg="bg.subtle">
      <PageHeader
        title="Feedback"
        description="What people sent with “Send feedback”, and the page they sent it from."
        breadcrumbs={breadcrumbs}
        actions={
          <Group attached>
            <Button size="sm" colorPalette="blue" variant={showAll ? "outline" : "solid"} onClick={() => setShowAll(false)}>
              Open{open.length === 0 ? "" : ` (${open.length})`}
            </Button>
            <Button size="sm" colorPalette="blue" variant={showAll ? "solid" : "outline"} onClick={() => setShowAll(true)}>
              All
            </Button>
          </Group>
        }
      />
      <Stack gap={2} px={{ base: 6, sm: 8, lg: 10 }} py={{ base: 6, lg: 8 }}>
        {access !== undefined && !access.isSysAdmin ? (
          <Text fontSize="sm" color="fg.muted">This page is for system administrators.</Text>
        ) : rows === undefined ? (
          <TableSkeleton rows={6} columns={4} />
        ) : (
          <>
            <HStack>
              <FilterCount
                shown={shown.length}
                total={rows.length}
                noun={["message", "messages"]}
                filtered={!showAll}
                onClear={() => setShowAll(true)}
              />
            </HStack>
            <Box borderWidth="1px" borderColor="border" bg="bg">
              <DataTable
                data={shown}
                columns={columns}
                getRowId={(row) => row._id}
                emptyMessage={showAll ? "Nobody has sent feedback yet." : "Nothing open."}
                interactive={false}
              />
            </Box>
          </>
        )}
      </Stack>
    </Box>
  );
}
